/*
 * halal-label/ing-core.js — Informasi Nilai Gizi (ING) by the book.
 *
 * Source of every rule here: Kepka BPOM No. 265 Tahun 2026, Lampiran II huruf B
 * (tata cara, p.196-201) and PerBPOM No. 10 Tahun 2026 (Pasal 8, 9, 20 and
 * Lampiran I). The worked example of the act — keripik tempe 150 g, takaran
 * saji 20 g -> 112 kkal, 7,3 g lemak, 128 mg natrium, 7 sajian — is the first
 * test in tests/js/halal-label-ing-core.test.mjs.
 *
 * WHY INTEGER ARITHMETIC. The act rounds "ke kelipatan 0,1 terdekat". With
 * floats, 20/100 x 12,25 = 2.4499999… and rounds DOWN to 2,4 where the act
 * means 2,5. Values in the table carry 2 decimals, the serving at most 1
 * (it is itself rounded to 0,5), so every product is an exact integer at a
 * fixed scale and half-up rounding is exact. User-entered lab values are
 * scaled to 4 decimals the same way.
 *
 * WHAT IS ROUNDED FROM WHAT. The act computes %AKG from the UNROUNDED amount
 * per serving (p.200: lemak total 7,268 g / 63 g = 11,536 % -> 12 %), not from
 * the printed 7,3 g. The only exception it names: an amount that prints as
 * 0,0 g / 0 mg shows 0 %.
 *
 * Pure module: no DOM. UMD so node --test can load it.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.HalalIng = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    var KEYS = ['energy', 'fat', 'sat', 'protein', 'carb', 'sugar', 'sodium'];
    var UNITS = { energy: 'kkal', fat: 'g', sat: 'g', protein: 'g', carb: 'g', sugar: 'g', sodium: 'mg' };
    // Kepka 265/2026 Lampiran II B.5.b — ALG kelompok umum.
    var ALG = { fat: 63, sat: 20, protein: 60, carb: 325, sodium: 1500 };
    // Whole numbers for energy and garam (natrium); one decimal for the rest.
    var DECIMALS = { energy: 0, fat: 1, sat: 1, protein: 1, carb: 1, sugar: 1, sodium: 0 };
    var SUGAR_MAX_G = 50;            // per takaran saji or per kemasan
    var VALUE_SCALE = 10000;         // per-100 values to 4 decimals
    var MASS_SCALE = 100;            // grams / ml to 2 decimals

    /* Half-up integer division for non-negative integers: round(a / b). */
    function divRound(a, b) {
        return Math.floor((2 * a + b) / (2 * b));
    }

    function toScaled(v, scale) {
        if (typeof v !== 'number' || !isFinite(v) || v < 0) return null;
        return Math.round(v * scale);
    }

    /* Round x to the nearest multiple of `step` (0.5 or 1), half up, exactly
       for inputs with up to 4 decimals. Returns a Number. */
    function roundToStep(x, step) {
        var xs = Math.round(x * 10000);
        var ss = Math.round(step * 10000);
        return divRound(xs, ss) * ss / 10000;
    }

    /* Is x exactly halfway between two multiples of `step`? (the act's own
       example: 7,5 sajian "dapat dibulatkan ke 7 atau 8") */
    function isTie(x, step) {
        var xs = Math.round(x * 10000);
        var ss = Math.round(step * 10000);
        return (2 * xs) % ss === 0 && (xs % ss) !== 0;
    }

    /* ── takaran saji ───────────────────────────────────────────────────── */

    /* Kepka II.A.2.d: a serving with a decimal is rounded to the nearest 0,5;
       II.A.2.c: if it is also written in URT (sendok, keping…) it must be a
       whole number. */
    function normalizeServing(g, withUrt) {
        if (typeof g !== 'number' || !isFinite(g) || g <= 0) return null;
        return withUrt ? roundToStep(g, 1) : roundToStep(g, 0.5);
    }

    /* Jumlah sajian per kemasan — Kepka II.B.3 / PerBPOM 10/2026 Lamp. I.2.c:
       >1..5 → nearest 0,5; >5 → nearest 1. A tie offers both (the act's
       example rounds 7,5 to "7 atau 8" and prints 7). */
    function servingsPerPack(netG, servingG) {
        if (!(netG > 0) || !(servingG > 0)) return null;
        var raw = netG / servingG;
        if (raw <= 1 + 1e-9) return { raw: raw, value: 1, single: true, options: [1] };
        var step = raw > 5 ? 1 : 0.5;
        var up = roundToStep(raw, step);
        if (isTie(raw, step)) {
            var down = up - step;
            if (down < 1) down = 1;
            return { raw: raw, value: down, tie: true, step: step, options: [down, up] };
        }
        return { raw: raw, value: up, step: step, options: [up] };
    }

    /* ── amounts ────────────────────────────────────────────────────────── */

    /* One nutrient: amount for `massG` of product from its per-100 value.
       Returns the exact unrounded amount (as a Number, for display of the
       working) and the rounded printed figure, plus %AKG where the act
       prints one. */
    function nutrient(key, per100, massG) {
        var v = toScaled(per100, VALUE_SCALE);
        var m = toScaled(massG, MASS_SCALE);
        if (v === null || m === null) return null;
        // amount = per100 * mass / 100  →  scaled by VALUE_SCALE*MASS_SCALE*100
        var prod = v * m;                               // amount * 1e6 * 100
        var denom = VALUE_SCALE * MASS_SCALE * 100;     // 1e8
        var dec = DECIMALS[key];
        var printedScaled = divRound(prod * Math.pow(10, dec), denom);
        var printed = printedScaled / Math.pow(10, dec);
        var out = {
            key: key,
            unit: UNITS[key],
            raw: prod / denom,
            value: printed,
            decimals: dec,
        };
        if (ALG[key]) {
            // %AKG from the UNROUNDED amount: prod/denom / ALG * 100
            out.akgRaw = (prod / denom) / ALG[key] * 100;
            out.akg = printed === 0 ? 0 : divRound(prod * 100, denom * ALG[key]);
        }
        return out;
    }

    /* ── the whole table ────────────────────────────────────────────────
     *
     * item: { name, v:[7 numbers per 100 g/ml] | null, serving:[min,max,unit]?,
     *         antara?, per100?, gap? }   (shape of resources/data/halal-label)
     * opts: { net: grams|ml in the pack, serving: chosen takaran saji,
     *         urt: string|'' , spp: chosen sajian per kemasan (tie choice),
     *         mode: 'auto'|'per100'|'perPack' }
     *
     * Returns { basis, massG, serving, spp, rows[], checks[], ok }.
     * `basis` is what the table is "per": 'serving' | 'pack' | '100'.
     * `checks` are findings with a code (texts live in the lang file) and a
     * level: 'error' blocks a lawful table, 'warn' asks a person to look.
     */
    function compute(item, opts) {
        opts = opts || {};
        var checks = [];
        var res = { item: item, rows: [], checks: checks, ok: false };
        if (!item) { checks.push({ code: 'no_item', level: 'error' }); return res; }
        if (item.gap === 'no-values' || !item.v) {
            checks.push({ code: 'gap_no_values', level: 'error' });
            return res;
        }
        var net = typeof opts.net === 'number' && opts.net > 0 ? opts.net : null;
        var unit = (item.serving && item.serving[2]) || item.per100 || 'g';
        res.unit = unit === 'g|ml' ? (opts.unit === 'ml' ? 'ml' : 'g') : unit;

        // Pangan olahan antara and the "Per 100 g" kinds: no serving at all.
        if (item.antara || item.per100) {
            res.basis = '100';
            res.massG = 100;
            if (item.antara && net && net < 100 && opts.mode === 'perPack') {
                // PerBPOM 10/2026 Pasal 9 ayat (4): antara under 100 g may be per kemasan.
                res.basis = 'pack';
                res.massG = net;
            }
            checks.push({ code: item.antara ? 'antara' : 'per100_only', level: 'info' });
            return finish(res, item, checks);
        }

        if (item.gap === 'no-serving' || !item.serving) {
            checks.push({ code: 'gap_no_serving', level: 'error' });
            return res;
        }
        var min = item.serving[0], max = item.serving[1];
        res.range = [min, max];

        if (!net) {
            checks.push({ code: 'need_net', level: 'error' });
            return res;
        }
        res.net = net;

        // Net content inside the serving range → it IS the serving (Pasal 8(3)).
        if (net >= min - 1e-9 && net <= max + 1e-9) {
            res.basis = 'serving';
            res.serving = net;
            res.single = true;
            res.massG = net;
            checks.push({ code: 'net_is_serving', level: 'info' });
            return finish(res, item, checks);
        }

        // Net content under one serving → per kemasan (Pasal 9(1)f, (5)),
        // and it must be at least half the smallest serving (Pasal 9(6)).
        if (net < min) {
            if (net < min / 2 - 1e-9) {
                checks.push({ code: 'net_below_half_min', level: 'error', min: min, half: min / 2 });
                return res;
            }
            res.basis = 'pack';
            res.massG = net;
            checks.push({ code: 'per_pack', level: 'info' });
            return finish(res, item, checks);
        }

        // More than one serving: the person picks one inside the range.
        var s = normalizeServing(typeof opts.serving === 'number' ? opts.serving : null, !!(opts.urt && String(opts.urt).trim()));
        if (s === null) {
            checks.push({ code: 'need_serving', level: 'error' });
            return res;
        }
        if (opts.serving !== s) checks.push({ code: 'serving_rounded', level: 'info', from: opts.serving, to: s });
        if (s < min - 1e-9 || s > max + 1e-9) {
            checks.push({ code: 'serving_out_of_range', level: 'error', min: min, max: max, serving: s });
            return res;
        }
        res.basis = 'serving';
        res.serving = s;
        res.massG = s;
        var spp = servingsPerPack(net, s);
        res.sppInfo = spp;
        if (spp) {
            var chosen = spp.value;
            if (spp.tie && typeof opts.spp === 'number' && spp.options.indexOf(opts.spp) >= 0) chosen = opts.spp;
            res.spp = chosen;
            if (spp.tie) checks.push({ code: 'spp_tie', level: 'info', options: spp.options, raw: spp.raw });
        }
        return finish(res, item, checks);
    }

    function finish(res, item, checks) {
        var rows = [];
        for (var i = 0; i < KEYS.length; i++) {
            var r = nutrient(KEYS[i], item.v[i], res.massG);
            rows.push(r);
        }
        res.rows = rows;
        var byKey = {};
        rows.forEach(function (r) { byKey[r.key] = r; });
        res.byKey = byKey;

        // Limits apply to what the table states per serving or per pack.
        // The per-100 g tables (antara, gula merah) state no serving, so
        // there is nothing a consumer eats "per" to compare against.
        if (res.basis === 'serving' || res.basis === 'pack') {
            var over = rows.filter(function (r) { return r.akg != null && r.akg > 100; });
            if (over.length) {
                checks.push({
                    code: 'over_100_alg', level: 'error',
                    keys: over.map(function (r) { return r.key; }),
                    maxServing: maxLawfulServing(item, res),
                });
            }
            if (byKey.sugar && byKey.sugar.value > SUGAR_MAX_G) {
                checks.push({ code: 'sugar_over_50', level: 'error', value: byKey.sugar.value,
                    maxServing: maxLawfulServing(item, res) });
            }
        }
        res.ok = !checks.some(function (c) { return c.level === 'error'; });
        return res;
    }

    /* Largest serving (multiple of 0,5) inside the allowed range for which
       every %AKG stays <= 100 and sugar <= 50 g. null if none — then the
       values of the act cannot make a lawful table for this kind, and the
       honest answer is a lab analysis (4 kinds of 465 hit this: ikan bilis
       goreng, ikan wader goreng, keripik bakso goreng, keripik bayam). */
    function maxLawfulServing(item, res) {
        if (!res.range) return null;
        var min = res.range[0], max = res.range[1];
        for (var s = Math.floor(max * 2) / 2; s >= min - 1e-9; s -= 0.5) {
            if (lawfulAt(item, s)) return s;
        }
        return null;
    }

    function lawfulAt(item, mass) {
        for (var i = 0; i < KEYS.length; i++) {
            var r = nutrient(KEYS[i], item.v[i], mass);
            if (r.akg != null && r.akg > 100) return false;
            if (r.key === 'sugar' && r.value > SUGAR_MAX_G) return false;
        }
        return true;
    }

    /* Number formatting the Indonesian way: decimal COMMA, no thousands
       separator inside a table cell (the act writes 1117,03 and 1500 mg). */
    function fmt(n, decimals) {
        if (n == null || !isFinite(n)) return '';
        var s = Number(n).toFixed(decimals == null ? 0 : decimals);
        return s.replace('.', ',');
    }

    /* Data lookups --------------------------------------------------------- */

    function normalizeName(s) {
        return String(s || '').toLowerCase()
            .normalize('NFD').replace(/[̀-ͯ]/g, '')
            .replace(/[^a-z0-9]+/g, ' ').trim();
    }

    /* How people TYPE a kind vs how the act spells it (QA 23.10.2026: «kripik
       tempe», «sambel», «nugget», «coklat», «frozen», «basreng» found nothing).
       Both the names and the query go through the same word map, so «Sus
       Kering Coklat» and «Cokelat Susu» meet on «cokelat». Colloquial schwa
       loss (kripik, krupuk) is the KR- rule below, not a list. */
    var WORD = {
        sambel: 'sambal', coklat: 'cokelat', nugget: 'naget', nuget: 'naget',
        frozen: 'beku', sirop: 'sirup', peyek: 'rempeyek', mie: 'mi',
    };
    /* Shop words for a whole kind: «basreng» is bakso goreng on every pouch. */
    var ALIAS = { basreng: 'bakso goreng', batagor: 'bakso tahu goreng' };
    function canonWord(w) {
        if (WORD[w]) return WORD[w];
        if (/^kr[iu]p/.test(w)) return 'ke' + w.slice(1);   // kripik → keripik, krupuk → kerupuk
        return w;
    }
    function canonText(s) {
        var out = [];
        normalizeName(s).split(' ').forEach(function (w) {
            if (!w) return;
            if (ALIAS[w]) out.push.apply(out, ALIAS[w].split(' '));
            else out.push(canonWord(w));
        });
        return out.join(' ');
    }

    /* Rank kinds for a typed query. Every word of the query must start a word
       of the name ("kerip tem" finds Keripik Tempe); a whole-name prefix ranks
       first. When no kind carries every word («keripik tempe pedas»), the
       kinds carrying the MOST words, the first one included, come back
       instead — a flavour word on the pouch is not another kind in the act.
       Returns the items, best first. */
    function search(items, query, limit) { return find(items, query, limit).items; }
    /* The same, saying whether the list is a near match (`partial`): the page
       titles it «mirip» so nobody takes Keripik Apel for keripik tempe pedas. */
    function find(items, query, limit) {
        var q = canonText(query);
        if (!q) return { items: [], partial: false };
        var qw = q.split(' ');
        var full = [], part = [];
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            var n = it._c || (it._c = canonText(it.name));
            var words = n.split(' ');
            var missed = 0, firstMissed = false, score = 0;
            for (var j = 0; j < qw.length; j++) {
                var hit = -1;
                for (var k = 0; k < words.length; k++) {
                    if (words[k].indexOf(qw[j]) === 0) { hit = k; break; }
                }
                if (hit < 0) {
                    if (n.indexOf(qw[j]) >= 0) { score += 3; continue; }
                    missed++;
                    if (j === 0) firstMissed = true;
                    continue;
                }
                score += hit;
            }
            if (!missed) {
                if (n.indexOf(q) === 0) score -= 10;
                full.push({ it: it, score: score + n.length / 100 });
            } else if (qw.length > 1 && missed < qw.length && !firstMissed) {
                part.push({ it: it, score: missed * 20 + score + n.length / 100 });
            }
        }
        var out = full.length ? full : part;
        out.sort(function (a, b) { return a.score - b.score; });
        return { items: out.slice(0, limit || 30).map(function (x) { return x.it; }), partial: !full.length && part.length > 0 };
    }

    return {
        KEYS: KEYS,
        UNITS: UNITS,
        ALG: ALG,
        DECIMALS: DECIMALS,
        SUGAR_MAX_G: SUGAR_MAX_G,
        divRound: divRound,
        roundToStep: roundToStep,
        isTie: isTie,
        normalizeServing: normalizeServing,
        servingsPerPack: servingsPerPack,
        nutrient: nutrient,
        compute: compute,
        maxLawfulServing: maxLawfulServing,
        fmt: fmt,
        normalizeName: normalizeName,
        search: search,
        find: find,
        canonText: canonText,
    };
});
